import { useState, useEffect, useRef } from 'react';
import { miaChatV2SocketService, MiaChatV2SocketService } from '../../MiaChat/socketService/miaChatSocketService';
import { SocketServiceConfig } from 'app/socketService';

export interface SocketStatus {
  isConnected: boolean;
  isSubscribed: boolean;
  activeSubscriptionCount: number;
}

export const useSocketStatus = (): SocketStatus => {
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [isSubscribed, setIsSubscribed] = useState<boolean>(false);
  const [activeSubscriptionCount, setActiveSubscriptionCount] = useState<number>(0);
  const socketServiceRef = useRef<MiaChatV2SocketService | null>(null);

  // Initialize socket service
  useEffect(() => {
    try {
      const wsUrl = process.env.REACT_APP_WS_URL;
      if (wsUrl) {
        const config: SocketServiceConfig = {
          url: wsUrl,
          autoConnect: true,
          reconnection: true,
          reconnectionAttempts: 5,
          reconnectionDelay: 1000,
          options: {
            path: '/ws/',
            transports: ['websocket', 'polling']
          }
        };
        
        socketServiceRef.current = miaChatV2SocketService(config);
        socketServiceRef.current.connect();
        setIsConnected(socketServiceRef.current.isConnected());
        
        // Set up connection event listeners
        const handleConnect = () => {
          setIsConnected(true);
        };

        const handleDisconnect = () => {
          setIsConnected(false);
          setIsSubscribed(false);
          setActiveSubscriptionCount(0);
        };

        socketServiceRef.current.on('connect', handleConnect);
        socketServiceRef.current.on('disconnect', handleDisconnect);

        return () => {
          socketServiceRef.current?.off('connect', handleConnect);
          socketServiceRef.current?.off('disconnect', handleDisconnect);
        };
      }
    } catch (error) {
      console.error('Failed to initialize socket service:', error);
    }
  }, []);

  // Monitor socket connection and subscription status
  useEffect(() => {
    const checkStatus = () => {
      if (socketServiceRef.current) {
        const connected = socketServiceRef.current.isConnected();
        setIsConnected(connected);
        
        if (connected) {
          const activeSubscriptions = socketServiceRef.current.getActiveSubscriptions();
          const subscriptionCount = activeSubscriptions.length;
          setActiveSubscriptionCount(subscriptionCount);
          setIsSubscribed(subscriptionCount > 0);
        } else {
          setIsSubscribed(false);
          setActiveSubscriptionCount(0);
        }
      } else {
        setIsConnected(false);
        setIsSubscribed(false);
        setActiveSubscriptionCount(0);
      }
    };

    // Check immediately
    checkStatus();
    
    // Then check periodically
    const interval = setInterval(checkStatus, 2000); // Check every 2 seconds
    return () => clearInterval(interval);
  }, []);

  return {
    isConnected,
    isSubscribed,
    activeSubscriptionCount
  };
};

